import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import StatusBadge from "./StatusBadge";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { WarningCircle, CheckCircle, XCircle, Lightning } from "@phosphor-icons/react";

const STEPS = ["verify", "nameservers", "preview", "execute"];

export default function BulkDnsModal({ open, onClose, domains, onCompleted }) {
  const [step, setStep] = useState(0);
  const [ns, setNs] = useState(["", "", "", ""]);
  const [notes, setNotes] = useState("");
  const [presets, setPresets] = useState([]);
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setStep(0);
      setResults([]);
      setError("");
      api.get("/dns-presets").then(({ data }) => setPresets(data.items || [])).catch(() => {});
    }
  }, [open]);

  const applyPreset = (p) => {
    const filled = [...p.nameservers, "", "", "", ""].slice(0, 4);
    setNs(filled);
    toast.success(`Applied ${p.name}`);
  };

  const validNs = ns.filter((x) => x.trim()).length >= 2 &&
    ns.every((x) => !x.trim() || /^[a-zA-Z0-9][a-zA-Z0-9\-.]*\.[a-zA-Z0-9][a-zA-Z0-9\-.]*$/.test(x.trim()));

  const execute = async () => {
    setRunning(true);
    setError("");
    // initialize results as pending
    setResults(domains.map((d) => ({ domain: d, status: "pending" })));
    try {
      const { data } = await api.post("/domains/bulk-dns", {
        domains,
        nameservers: ns.map((x) => x.trim()).filter(Boolean),
        notes,
      });
      setResults((data.results || []).map((r) => ({
        domain: r.domain, status: r.success ? "success" : "failed", message: r.message,
      })));
      if (data.failed === 0) toast.success(`All ${data.success} domains updated`);
      else toast.warning(`${data.success} succeeded, ${data.failed} failed`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  const retryFailed = async () => {
    const failedDomains = results.filter((r) => r.status === "failed").map((r) => r.domain);
    if (!failedDomains.length) return;
    setRunning(true);
    try {
      const { data } = await api.post("/domains/bulk-dns", {
        domains: failedDomains,
        nameservers: ns.map((x) => x.trim()).filter(Boolean),
        notes: notes + " [retry]",
      });
      const map = new Map((data.results || []).map((r) => [r.domain, r]));
      setResults((prev) => prev.map((r) =>
        map.has(r.domain) ? { ...r, status: map.get(r.domain).success ? "success" : "failed", message: map.get(r.domain).message } : r
      ));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setRunning(false); }
  };

  const close = () => {
    if (running) return;
    if (step === STEPS.length - 1 && results.length) onCompleted?.();
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-2xl" data-testid="bulk-dns-modal">
        <DialogHeader>
          <div className="overline mb-1">Bulk DNS Change · Step {step + 1} of {STEPS.length}</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {step === 0 && "Verify selected domains"}
            {step === 1 && "Configure nameservers"}
            {step === 2 && "Preview changes"}
            {step === 3 && "Execution"}
          </DialogTitle>
        </DialogHeader>

        {/* Step 0 */}
        {step === 0 && (
          <div className="space-y-4">
            <div className="p-3 border border-warning/30 bg-warning/10 rounded-md text-xs flex gap-2">
              <WarningCircle size={16} className="text-warning shrink-0 mt-0.5" />
              <div>
                Changing to custom nameservers may disable Namecheap features like email forwarding,
                URL redirects, and DDNS. Only proceed if your target DNS provider is ready.
              </div>
            </div>
            <div className="max-h-64 overflow-auto border border-border rounded-md">
              <ul className="divide-y divide-border">
                {domains.map((d) => (
                  <li key={d} className="px-4 py-2 font-mono text-sm" data-testid={`verify-${d}`}>{d}</li>
                ))}
              </ul>
            </div>
            <div className="text-xs text-muted-foreground">{domains.length} domains selected.</div>
          </div>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <div className="space-y-4">
            {presets.length > 0 && (
              <div>
                <div className="overline mb-2">Presets</div>
                <div className="flex flex-wrap gap-2">
                  {presets.map((p) => (
                    <Button
                      key={p.id} variant="outline" size="sm"
                      onClick={() => applyPreset(p)}
                      data-testid={`preset-${p.id}`}
                    >
                      <Lightning size={12} className="mr-1.5" /> {p.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="space-y-1.5">
                <Label className="overline">Nameserver {i + 1}{i < 2 ? " *" : ""}</Label>
                <Input
                  value={ns[i]}
                  onChange={(e) => setNs(ns.map((x, j) => (j === i ? e.target.value : x)))}
                  placeholder={`ns${i + 1}.example.com`}
                  className="font-mono text-sm"
                  data-testid={`ns-input-${i}`}
                />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="overline">Notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ns-notes" />
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <div className="overline mb-2">Nameservers to apply</div>
              <div className="font-mono text-sm bg-muted/40 p-3 rounded-md">
                {ns.filter(Boolean).map((n, i) => <div key={i}>{i + 1}. {n}</div>)}
              </div>
            </div>
            <div>
              <div className="overline mb-2">Affected Domains · {domains.length}</div>
              <div className="max-h-48 overflow-auto border border-border rounded-md p-2">
                <div className="grid grid-cols-2 gap-1 text-xs font-mono">
                  {domains.map((d) => <div key={d}>{d}</div>)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="space-y-3">
            {error && <div className="text-xs text-destructive">{error}</div>}
            <div className="max-h-80 overflow-auto border border-border rounded-md">
              <ul className="divide-y divide-border">
                {(results.length ? results : domains.map((d) => ({ domain: d, status: "pending" }))).map((r) => (
                  <li key={r.domain} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`result-${r.domain}`}>
                    <span className="font-mono text-xs">{r.domain}</span>
                    {r.status === "success" && <StatusBadge variant="success" icon={CheckCircle}>updated</StatusBadge>}
                    {r.status === "failed" && (
                      <span className="flex items-center gap-2">
                        <StatusBadge variant="danger" icon={XCircle}>failed</StatusBadge>
                        <span className="text-[11px] text-muted-foreground max-w-xs truncate">{r.message}</span>
                      </span>
                    )}
                    {r.status === "pending" && <StatusBadge>pending</StatusBadge>}
                  </li>
                ))}
              </ul>
            </div>
            {results.some((r) => r.status === "failed") && (
              <Button variant="outline" size="sm" onClick={retryFailed} disabled={running} data-testid="retry-failed-btn">
                Retry failed
              </Button>
            )}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={close} disabled={running} data-testid="bulk-cancel-btn">
            {step === STEPS.length - 1 ? "Done" : "Cancel"}
          </Button>
          <div className="flex gap-2">
            {step > 0 && step < STEPS.length - 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="bulk-back-btn">Back</Button>
            )}
            {step < 2 && (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !validNs}
                data-testid="bulk-next-btn"
              >
                Next
              </Button>
            )}
            {step === 2 && (
              <Button
                onClick={() => { setStep(3); execute(); }}
                data-testid="bulk-confirm-btn"
              >
                Confirm & apply
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
